import { isSupabaseConfigured } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import type { AppUser } from "@/features/auth/types";
import type { SettlementRecord, SettlementStatus } from "./types";

interface SettlementRow {
  id: string;
  carrier_profile_id: string | null;
  reference: string;
  period_start: string;
  period_end: string;
  amount_cents: number;
  status: SettlementStatus;
  notes: string | null;
  created_at: string;
}

const LS_KEY = "bb_settlements";

const seedSettlements: SettlementRecord[] = [
  {
    id: "settle-2401",
    carrierProfileId: "profile-carrier",
    reference: "SET-2026-W23",
    periodStart: "2026-06-02",
    periodEnd: "2026-06-08",
    amountCents: 482_500,
    status: "paid",
    notes: "Direct deposit 6/10.",
    createdAt: "2026-06-10T16:00:00.000Z",
  },
  {
    id: "settle-2402",
    carrierProfileId: "profile-carrier",
    reference: "SET-2026-W24",
    periodStart: "2026-06-09",
    periodEnd: "2026-06-15",
    amountCents: 513_750,
    status: "pending",
    notes: "Processing - expect payment by 6/17.",
    createdAt: "2026-06-16T12:00:00.000Z",
  },
];

const supabaseReady = () => isSupabaseConfigured && !!supabase;

function previewProfileId(userId: string): string {
  if (userId.startsWith("dev-carrier")) return "profile-carrier";
  if (userId.startsWith("dev-")) return userId.replace("dev-", "profile-");
  return userId;
}

function rowToSettlement(row: SettlementRow): SettlementRecord {
  return {
    id: row.id,
    carrierProfileId: row.carrier_profile_id,
    reference: row.reference,
    periodStart: row.period_start,
    periodEnd: row.period_end,
    amountCents: row.amount_cents,
    status: row.status,
    notes: row.notes ?? "",
    createdAt: row.created_at,
  };
}

function localReadAll(): SettlementRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw) as SettlementRecord[];
  } catch {
    /* ignore malformed cache */
  }
  localStorage.setItem(LS_KEY, JSON.stringify(seedSettlements));
  return [...seedSettlements];
}

const byNewest = (a: SettlementRecord, b: SettlementRecord) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

function filterForViewer(records: SettlementRecord[], viewer?: AppUser | null): SettlementRecord[] {
  if (!viewer) return records;
  if (viewer.role === "internal") return records;
  if (viewer.role === "carrier") {
    const profileId = previewProfileId(viewer.id);
    return records.filter((record) => record.carrierProfileId === profileId);
  }
  return [];
}

/** Owner-operator settlement statements stored by BB (not an external API yet). */
export async function listSettlements(viewer?: AppUser | null): Promise<SettlementRecord[]> {
  if (supabaseReady()) {
    const { data, error } = await supabase!
      .from("settlements")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw error;
    return filterForViewer((data as SettlementRow[]).map(rowToSettlement), viewer);
  }
  return filterForViewer(localReadAll(), viewer).sort(byNewest);
}

export function formatSettlementAmount(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

export type { SettlementRecord, SettlementStatus } from "./types";
